import { useCallback, useRef, useState } from 'react';
import { UploadCloud } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import type { IntakeToday } from '../types/documentCapture.types';

/**
 * Today's card + manual drop zone (86d3rrfbf).
 *
 * The drop zone is ALWAYS present, not just when the watcher is broken: it's the fallback for
 * non-Chromium browsers, for a lost folder, and for the one-off scan somebody has on a laptop.
 * It feeds the exact same intake endpoint the watcher uses, so a manually dropped file gets the
 * identical journey.
 *
 * Rendered only for users holding `docscan.upload` — the endpoint is gated on it, so showing the
 * zone to a viewer would just produce a 403 on drop.
 *
 * 86d3yek7d — this said "Today's session" and none of the numbers were today's, or a session's.
 * "scanned" was a counter living on this browser tab, so it read 0 again after every reload; the rest
 * were derived from the live feed's rolling ~50-EVENT window, which holds only the last 10-20 files,
 * is wiped whenever the API restarts, and is filtered per user — so two people saw two different
 * "todays", and the card genuinely changed on every reload. The owner noticed and was right. The
 * numbers now come from a day-scoped server count, and every chip carries a tooltip saying what it
 * measures, because "captured" as a STATE and "captured" as an EVENT are different numbers and only
 * the first is buildable without new timestamps in the schema.
 */

interface SessionCardProps {
  /** Server-counted intake for the current Philippine day; null until the first fetch lands. */
  today: IntakeToday | null;
  /** First load in flight — chips show skeletons rather than a provisional, wrong 0. */
  loading: boolean;
  /** Set when today's numbers could not be fetched; the card offers a retry instead of showing zeros. */
  error: string | null;
  onRetryToday: () => void;
  /** Promotes the zone visually when automatic capture isn't running. */
  emphasised: boolean;
  onFiles: (files: File[]) => void;
}

export function SessionCard({
  today,
  loading,
  error,
  onRetryToday,
  emphasised,
  onFiles,
}: SessionCardProps) {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFiles = useCallback(
    (list: FileList | null) => {
      if (!list) return;
      const pdfs = Array.from(list).filter((f) => f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf'));
      if (pdfs.length) onFiles(pdfs);
    },
    [onFiles]
  );

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Today</CardTitle>
        {/* The scope, stated once, so the chips do not each have to carry it. "Every station" is the
            change people will feel: this used to be one browser tab's count. */}
        <p className="text-xs text-muted-foreground">
          Every station, since midnight (Manila).
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* The error state replaces the chips ONLY when there is nothing to show. A refresh that
            fails after a good load leaves the last real numbers on screen — they are still the
            truth as of a moment ago, and blanking them for a background failure is exactly the
            "never blank what you already know" rule (CLAUDE.md). The next scan refetches anyway. */}
        {error && today === null ? (
          <div className="flex items-center justify-between gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2">
            <p className="text-xs text-muted-foreground">Today’s numbers didn’t load.</p>
            <Button variant="outline" size="sm" className="h-7 text-xs" onClick={onRetryToday}>
              Retry
            </Button>
          </div>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            <TallyChip
              label="scanned"
              value={today?.scanned ?? null}
              loading={loading}
              tip="Files uploaded today from every scan station, since midnight (Manila). Re-scanning a page DOP already has doesn’t add to this — the same file is recognised and no new copy is made."
            />
            <TallyChip
              label="captured"
              value={today?.captured ?? null}
              loading={loading}
              tone="success"
              tip="How many of today’s uploads are now filed against a document. It’s where they stand right now, so it keeps rising as the ones still being read come through."
            />
            <TallyChip
              label="linked"
              value={today?.linked ?? null}
              loading={loading}
              tone="success"
              tip="How many of today’s uploads are attached to their NetSuite transaction right now. Linking can land after midnight, in which case the scan counts on the day it was uploaded, not the day it linked."
            />
            {today != null && today.failed > 0 && (
              <TallyChip
                label="failed"
                value={today.failed}
                loading={false}
                tone="destructive"
                tip="Today’s uploads whose barcode couldn’t be read and that nobody has filed yet. They’re in the review queue; once someone identifies one it stops counting here."
              />
            )}
          </div>
        )}

        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            handleFiles(e.dataTransfer.files);
          }}
          className={cn(
            'rounded-lg border-2 border-dashed p-4 text-center transition-colors',
            dragging
              ? 'border-primary bg-primary/5'
              : emphasised
                ? 'border-info/40 bg-info/5'
                : 'border-border bg-muted/40'
          )}
        >
          <UploadCloud className="mx-auto h-6 w-6 text-muted-foreground" aria-hidden />
          <p className="mt-2 text-sm text-foreground">
            {emphasised ? 'Drop today’s scans here' : 'Or drop files here'}
          </p>
          <p className="mt-0.5 text-xs text-muted-foreground">PDF only — same handling as the watched folder.</p>
          <Button variant="outline" size="sm" className="mt-2" onClick={() => inputRef.current?.click()}>
            Choose files
          </Button>
          <input
            ref={inputRef}
            type="file"
            accept="application/pdf,.pdf"
            multiple
            className="hidden"
            onChange={(e) => {
              handleFiles(e.target.files);
              // Reset so re-picking the same file still fires a change event.
              e.target.value = '';
            }}
          />
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * One number and what it means. The tooltip is not decoration (86d3yek7d): every chip here is a
 * state-of-today's-uploads count, which is NOT the event count the word "captured" suggests, and the
 * card is the surface an owner reads to answer "how did today go".
 */
function TallyChip({
  label,
  value,
  loading,
  tip,
  tone = 'muted',
}: {
  label: string;
  /** null = unknown (loading, or the fetch failed) — never rendered as 0. */
  value: number | null;
  loading: boolean;
  tip: string;
  tone?: 'muted' | 'success' | 'destructive';
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {/* A <div>, not the <span> this used to be: the loading state renders the shared Skeleton
            primitive, which is a <div>, and a <div> inside a <span> is invalid phrasing content.
            Inside a flex container both blockify identically, so nothing moves. */}
        <div
          className={cn(
            'inline-flex cursor-help items-center gap-1 rounded-full border px-2 py-0.5 text-xs',
            tone === 'success'
              ? 'border-success/25 bg-success/10 text-success'
              : tone === 'destructive'
                ? 'border-destructive/25 bg-destructive/10 text-destructive'
                : 'border-border bg-muted text-muted-foreground'
          )}
        >
          {loading && value === null ? (
            <Skeleton className="h-3 w-4" />
          ) : (
            <span className="font-semibold tabular-nums">{value ?? '—'}</span>
          )}
          {label}
        </div>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">{tip}</TooltipContent>
    </Tooltip>
  );
}
